import React from "react";
import { Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  Email as EmailIcon,
  InsertDriveFile as DocIcon,
} from "@mui/icons-material";
import {
  Column,
  createColumn,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";
import {
  DetentionLumperStatus,
  DetentionLumperStatusLabel,
} from "../../../types/detention";
import { STATUS_COLORS } from "../../../utils/statusColors";

export const STATUS_CHIP_STYLE: Record<
  DetentionLumperStatus,
  { backgroundColor: string; color: string }
> = {
  0: { backgroundColor: STATUS_COLORS.pending, color: "#fff" },
  1: { backgroundColor: STATUS_COLORS.success, color: "#fff" },
  2: { backgroundColor: STATUS_COLORS.inactive, color: "#fff" },
};

export interface DetentionLumperTableRow {
  id: number;
  loadNumber: string;
  brokerEmail: string;
  vehicleId: number;
  vehicleName?: string;
  trailerId: number;
  trailerUnit?: string;
  amount?: number;
  status: DetentionLumperStatus;
  emailSent: boolean;
  documentUrl?: string;
  documentFileName?: string;
}

export const formatDateTime = (iso?: string) =>
  iso ? new Date(iso).toLocaleString() : "—";

export const calcDuration = (timeIn: string, timeOut: string) => {
  const ms = new Date(timeOut).getTime() - new Date(timeIn).getTime();
  if (ms <= 0) return "—";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
};

const cellText = (value: React.ReactNode) => (
  <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
    {value}
  </Typography>
);

export const createLoadNumberColumn = (): Column =>
  createColumn("loadNumber", "Load #", {
    width: 110,
    renderCell: (value: string) => cellText(value),
  });

export const createBrokerEmailColumn = (): Column =>
  createColumn("brokerEmail", "Broker Email", {
    flex: 1,
    minWidth: 160,
    renderCell: (value: string) => (
      <Tooltip title={value}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontSize: "0.8125rem", maxWidth: 160 }}
        >
          {value}
        </Typography>
      </Tooltip>
    ),
  });

export const createVehicleColumn = (): Column =>
  createColumn("vehicleName", "Vehicle", {
    flex: 1,
    minWidth: 120,
    renderCell: (_value: string | undefined, row: DetentionLumperTableRow) =>
      cellText(row.vehicleName || `#${row.vehicleId}`),
  });

export const createTrailerColumn = (): Column =>
  createColumn("trailerUnit", "Trailer", {
    width: 110,
    renderCell: (_value: string | undefined, row: DetentionLumperTableRow) =>
      cellText(row.trailerUnit || `#${row.trailerId}`),
  });

export const createAmountColumn = (): Column =>
  createColumn("amount", "Amount", {
    width: 100,
    renderCell: (value: number | undefined) =>
      cellText(value != null ? `$${value.toFixed(2)}` : "—"),
  });

export const createStatusColumn = (): Column =>
  createColumn("status", "Status", {
    width: 110,
    renderCell: (value: DetentionLumperStatus) => (
      <Chip
        label={DetentionLumperStatusLabel[value]}
        size="small"
        style={STATUS_CHIP_STYLE[value]}
      />
    ),
  });

export const createEmailSentColumn = (): Column =>
  createColumn("emailSent", "Email", {
    width: 70,
    disableTooltip: true,
    renderCell: (value: boolean) => (
      <Tooltip title={value ? "Email sent" : "Email not sent"}>
        <EmailIcon fontSize="small" color={value ? "success" : "disabled"} />
      </Tooltip>
    ),
  });

export const createDocumentColumn = (label = "Doc"): Column =>
  createColumn("documentUrl", label, {
    width: 70,
    disableTooltip: true,
    renderCell: (_value: string | undefined, row: DetentionLumperTableRow) =>
      row.documentUrl ? (
        <Tooltip title={row.documentFileName || "View document"}>
          <IconButton
            size="small"
            component="a"
            href={row.documentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <DocIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      ) : (
        cellText("—")
      ),
  });

export const createDateTimeColumn = (
  field: string,
  headerName: string,
  width = 160
): Column =>
  createColumn(field, headerName, {
    width,
    renderCell: (value: string) => (
      <Typography
        variant="body2"
        sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}
      >
        {formatDateTime(value)}
      </Typography>
    ),
  });

const SEARCHABLE_FIELDS = [
  "loadNumber",
  "brokerEmail",
  "vehicleName",
  "trailerUnit",
] as const;

export const createDetentionLumperSearchableColumns = (
  columns: Column[]
): SearchableColumn[] =>
  makeColumnsSearchable(columns, [...SEARCHABLE_FIELDS], {
    loadNumber: "string",
    brokerEmail: "string",
    vehicleName: "string",
    trailerUnit: "string",
  });

const getSearchableValue = (
  row: DetentionLumperTableRow,
  field: string
): string => {
  switch (field) {
    case "vehicleName":
      return row.vehicleName || String(row.vehicleId);
    case "trailerUnit":
      return row.trailerUnit || String(row.trailerId);
    default: {
      const value = row[field as keyof DetentionLumperTableRow];
      return value != null ? String(value) : "";
    }
  }
};

export const filterDetentionLumperRecords = <T extends DetentionLumperTableRow>(
  records: T[],
  filters: Record<string, any>
): T[] => {
  const activeFilters = Object.entries(filters).filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );

  if (activeFilters.length === 0) {
    return records;
  }

  return records.filter((record) =>
    activeFilters.every(([field, filterValue]) =>
      getSearchableValue(record, field)
        .toLowerCase()
        .includes(String(filterValue).toLowerCase())
    )
  );
};
